import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { cn } from "../../utils/common/cn";

/** A FilterBar-looking dropdown (label above, same trigger) whose options can be
 *  ticked independently. `allLabel` is the first item and selects/clears everything;
 *  `groupOf` optionally inserts a heading whenever the group changes between
 *  consecutive options. */
export function MultiSelectFilter<T extends string>({
  label,
  options,
  selected,
  onChange,
  allLabel,
  noneLabel = "None Selected",
  pluralNoun,
  groupOf,
  className,
}: {
  label: string;
  options: readonly T[];
  selected: ReadonlySet<T>;
  onChange: (next: Set<T>) => void;
  allLabel: string;
  noneLabel?: string;
  pluralNoun: string;
  groupOf?: (option: T) => string | undefined;
  className?: string;
}) {
  const allSelected = options.length > 0 && options.every((o) => selected.has(o));
  const picked = options.filter((o) => selected.has(o));
  const triggerLabel = allSelected
    ? allLabel
    : picked.length === 0
      ? noneLabel
      : picked.length === 1
        ? picked[0]!
        : `${picked.length} ${pluralNoun}`;

  function toggle(option: T) {
    const next = new Set(selected);
    if (next.has(option)) next.delete(option);
    else next.add(option);
    onChange(next);
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className="flex h-9 w-52 items-center justify-between gap-2 border border-border bg-secondary px-3 text-sm outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[320px] w-52 overflow-y-auto">
          <DropdownMenuCheckboxItem
            checked={allSelected}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => onChange(allSelected ? new Set() : new Set(options))}
          >
            {allLabel}
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          {options.map((option, i) => {
            const group = groupOf?.(option);
            const showHeading =
              group !== undefined && (i === 0 || group !== groupOf?.(options[i - 1] as T));
            return (
              <div key={option}>
                {showHeading ? (
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {group}
                  </DropdownMenuLabel>
                ) : null}
                <DropdownMenuCheckboxItem
                  checked={selected.has(option)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={() => toggle(option)}
                >
                  {option}
                </DropdownMenuCheckboxItem>
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
